// The inquiry items board: every existing item as a card + an "add item"
// placeholder that expands into the wizard. Server-truth rendering — every
// mutation invalidates the inquiry detail query (the hooks do it) and the
// board re-renders from the refetched data. At most one card is expanded at
// a time ('new' = the placeholder, a number = that item id being edited).

import { useDeleteRestaurantInquiryItemApi, useGetRestaurantInquiryApi } from 'apis/restaurant-inquiries';
import { Box, Flex, SimpleGrid, Skeleton, SkeletonText, Spinner, Text, useToast } from 'design-system';
import { isAppItem } from 'pages/inquiry-processor/utils/app-request';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { useEffect, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { ItemCard } from './item-card';
import { ItemWizard } from './item-wizard';

interface ItemCardsBoardProps {
  inquiryId: number | string;
}

// Card-shaped stand-in for a just-created item: the POST succeeded but the
// invalidated inquiry query is still refetching, so the real card isn't in
// `items` yet — without this the new item briefly "disappears" after save.
const SkeletonItemCard = () => (
  <Box borderWidth='1px' rounded='md' p='3' bg='white'>
    <SkeletonText noOfLines={2} spacing='2' skeletonHeight='3' w='70%' />
    <Skeleton mt='4' h='3' w='85%' />
    <Skeleton mt='2' h='3' w='55%' />
  </Box>
);

export const ItemCardsBoard = ({ inquiryId }: ItemCardsBoardProps) => {
  const toast = useToast();
  const { data: inquiry, isLoading, isFetching } = useGetRestaurantInquiryApi(inquiryId);
  const deleteItem = useDeleteRestaurantInquiryItemApi();
  const [expanded, setExpanded] = useState<'new' | number | null>(null);

  // Set when the wizard reports a successful create; the mutation hook has
  // already invalidated the inquiry query by then, so isFetching is true and
  // the skeleton clears when that refetch settles (success or error).
  const [pendingAdd, setPendingAdd] = useState(false);
  useEffect(() => {
    if (pendingAdd && !isFetching) setPendingAdd(false);
  }, [pendingAdd, isFetching]);

  const items = inquiry?.items ?? [];

  const onDelete = async (itemId: number, name: string) => {
    // Same confirmation pattern as the detail screen.
    if (!window.confirm(`Remove “${name}” from this inquiry? This cannot be undone.`)) return;
    try {
      await deleteItem.mutateAsync({ id: inquiryId, itemId });
    } catch (err) {
      toast({ title: 'Failed to remove item', description: apiErrorMessage(err), status: 'error' });
      return;
    }
    if (expanded === itemId) setExpanded(null);
  };

  if (isLoading) {
    return (
      <Flex justify='center' py='10'>
        <Spinner />
      </Flex>
    );
  }

  return (
    <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing='3'>
      {items.map((item) =>
        expanded === item.id ? (
          <Box key={item.id} gridColumn='1 / -1'>
            <ItemWizard
              inquiryId={inquiryId}
              item={item}
              excludeIds={items.filter((i) => i.id !== item.id).map((i) => i.masterProductId)}
              onClose={() => setExpanded(null)}
            />
          </Box>
        ) : (
          <ItemCard
            key={item.id}
            item={item}
            onEdit={() => setExpanded(item.id)}
            onDelete={
              isAppItem(item)
                ? undefined
                : () => onDelete(item.id, item.masterProduct?.name || `Product #${item.masterProductId}`)
            }
            isDeleting={deleteItem.isLoading}
          />
        )
      )}
      {pendingAdd && <SkeletonItemCard />}
      {expanded === 'new' ? (
        <Box gridColumn='1 / -1'>
          <ItemWizard
            inquiryId={inquiryId}
            excludeIds={items.map((i) => i.masterProductId)}
            onClose={() => setExpanded(null)}
            onSaved={() => setPendingAdd(true)}
          />
        </Box>
      ) : (
        <Flex
          as='button'
          type='button'
          onClick={() => setExpanded('new')}
          direction='column'
          align='center'
          justify='center'
          gap='2'
          minH='96px'
          borderWidth='2px'
          borderStyle='dashed'
          borderColor='gray.300'
          rounded='md'
          color='gray.500'
          _hover={{ borderColor: 'red.300', color: 'red.500' }}>
          <FiPlus size={20} />
          <Text fontSize='sm' fontWeight='medium'>
            Add item
          </Text>
        </Flex>
      )}
    </SimpleGrid>
  );
};
